import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router';
import { IonicVue, IonPage, IonHeader, IonToolbar, IonTitle, 
         IonContent, IonButtons, IonButton, IonIcon, IonBadge, 
         IonModal, IonItem, IonList, IonLabel, 
         IonAccordion,
         IonAccordionGroup,
         IonGrid,
         IonCol,
         IonRow,
         IonRange,
         IonInput,
         IonChip,
         IonCard,
         IonCardHeader,
         IonCardContent,
         IonProgressBar,
         IonSpinner} from '@ionic/vue';
import { addIcons } from 'ionicons';
import { close, closeCircle } from 'ionicons/icons';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { setupOptInSubscription, directWebhookTest, testOptIn } from './services/softPull';
import { supabase } from './supabase';
import { appUpdateService } from './services/appUpdateService'

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

// Import Ionic CSS
import '@ionic/vue/css/core.css'
import '@ionic/vue/css/normalize.css'
import '@ionic/vue/css/structure.css'
import '@ionic/vue/css/typography.css'

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

/* @import '@ionic/vue/css/palettes/dark.always.css'; */
/* @import '@ionic/vue/css/palettes/dark.class.css'; */
/* import '@ionic/vue/css/palettes/dark.system.css'; */

import { AppUpdate } from '@capawesome/capacitor-app-update';
/* Theme variables */
import './theme/variables.css';

/* Custom modal styles for light and dark mode */
/*import './theme/modals.css';*/

// Register Ionicons
addIcons({
  'close': close,
  'close-circle': closeCircle
});

const app = createApp(App)
const pinia = createPinia()

app
  .use(IonicVue)
  .use(router)
  .use(pinia)
  .component('IonCard', IonCard)
  .component('IonCardHeader', IonCardHeader)
  .component('IonCardContent', IonCardContent)
  .component('IonProgressBar', IonProgressBar);

// Register Ionic components globally
const components = {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonButtons, IonButton, IonIcon, IonBadge, IonModal,
  IonItem, IonList, IonLabel, IonAccordion, IonAccordionGroup,
  IonGrid, IonCol, IonRow, IonRange, IonInput, IonChip, IonSpinner
};

Object.entries(components).forEach(([name, component]) => {
  app.component(name, component);
});

router.isReady().then(() => {
  app.mount('#app');

  // Initialize the opt-in subscription after app is mounted
  console.log('MAIN: About to initialize opt-in subscription');
  try {
    const subscription = setupOptInSubscription();
    // Store the subscription in window for debugging
    (window as any).__optin_subscription = subscription;
    console.log('MAIN: Opt-in subscription initialized successfully');
    
    // Test subscription with a direct Supabase query to verify Realtime is working
    console.log('MAIN: Testing Realtime by directly inserting a test record');
    // Create a test record in any table with realtime enabled
    setTimeout(async () => {
      try {
        console.log('MAIN: Testing Supabase connection');
        // Use a simpler query that's more likely to work with limited permissions
        const { data, error } = await supabase.from('customer_data').select('created_at').limit(1);
        
        if (error) {
          console.error('MAIN: Supabase connection test failed:', error);
          
          // Try another approach - check auth status
          const { data: authData } = await supabase.auth.getSession();
          console.log('MAIN: Auth session check:', authData ? 'Session exists' : 'No session');
        } else {
          console.log('MAIN: Supabase connection test succeeded!');
        }
        console.log('MAIN: Supabase connection test result:', { data, error });
      } catch (testError) {
        console.error('MAIN: Error testing Supabase connection:', testError);
      }
    }, 2000); // Wait 2 seconds to make sure subscription is ready
  } catch (error) {
    console.error('MAIN: Failed to initialize opt-in subscription:', error);
  }

});

// Initialize push notifications
const initializePushNotifications = async () => {
  if (Capacitor.getPlatform() === 'web') {
    console.log('Push notifications not available in browser')
    return
  }

  try {
    const permission = await PushNotifications.requestPermissions()
    if (permission.receive === 'granted') {
      await PushNotifications.register()
    }
  } catch (error) {
    console.error('Error initializing push notifications:', error)
  }
}

// Global function for direct testing from console
(window as any).testWebhook = (customerId: number, rooftopId: string) => {
  console.log('CONSOLE TEST: Starting direct webhook test');
  return directWebhookTest(customerId, rooftopId);
};

// Add global test function for opt-in changes
(window as any).testOptInChange = (customerId: number, rooftopId: string) => {
  console.log('CONSOLE TEST: Starting opt-in status change test');
  return testOptIn(customerId, rooftopId);
};

// Add a minimal test function to check if we can reach the webhook
(window as any).basicWebhookTest = async () => {
  console.log('MINIMAL TEST: Making a basic fetch request to webhook URL');
  
  try {
    const webhookUrl = import.meta.env.VITE_PIPEDREAM_WEBHOOK_URL;
    console.log('MINIMAL TEST: Using webhook URL:', webhookUrl);
    
    // Log the token prefix to check if it looks valid (without exposing the full token)
    const token = import.meta.env.VITE_API_BEARER_TOKEN || '';
    console.log('MINIMAL TEST: Token prefix:', token.substring(0, 5) + '...');
    
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Source': 'grip-frontend-test'
      },
      // Send a more complete payload that matches what the webhook expects
      body: JSON.stringify({ 
        customerId: 99999, // Use a fake ID that won't impact real data
        action: 'credit_check',
        first_name: 'Test',
        last_name: 'User',
        address: '123 Test St',
        city: 'Testville',
        state: 'TS',
        zip: '12345',
        test: true
      })
    });
    
    console.log('MINIMAL TEST: Response status:', response.status);
    const responseText = await response.text();
    console.log('MINIMAL TEST: Response body:', responseText);
    
    // Even with a 400, we'll consider it successful for connectivity testing
    // since it means we reached the endpoint
    return { 
      success: true, // Consider it successful if we got any response
      actualSuccess: response.ok,
      status: response.status, 
      body: responseText 
    };
  } catch (error) {
    console.error('MINIMAL TEST: Error:', error);
    return { success: false, error: String(error) };
  }
};

// Run all diagnostic tests in sequence
(window as any).runAllTests = async (customerId: number, rooftopId: string) => {
  console.log('DIAGNOSTIC: Starting full diagnostic sequence');
  
  const results: Record<string, any> = {};
  
  try {
    // Step 1: Test basic webhook connectivity
    console.log('DIAGNOSTIC: Step 1 - Testing webhook connectivity');
    const webhookResult = await (window as any).basicWebhookTest();
    console.log('DIAGNOSTIC: Webhook test result:', webhookResult.success ? 'PASSED' : 'FAILED', webhookResult);
    results.webhookTest = webhookResult;
    
    // Wait 2 seconds before next test
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Step 2: Test Supabase connectivity
    console.log('DIAGNOSTIC: Step 2 - Testing Supabase connectivity');
    try {
      // First try a simple health check
      console.log('SUPABASE TEST: Checking auth session directly');
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      console.log('SUPABASE TEST: Auth session result:', { 
        hasSession: sessionData && sessionData.session ? true : false,
        error: sessionError ? String(sessionError) : null
      });
      
      // Check if we can connect at all
      console.log('SUPABASE TEST: Testing simple connectivity');
      const { data, error } = await supabase.from('service_data').select('count').limit(1);
      
      const supabaseResult = {
        success: !error && !sessionError,
        authOk: !sessionError,
        dataOk: !error,
        authData: sessionData ? 'Has data' : 'No data',
        error: error ? String(error) : null
      };
      
      console.log('DIAGNOSTIC: Supabase test result:', supabaseResult.success ? 'PASSED' : 'FAILED', supabaseResult);
      results.supabaseTest = supabaseResult;
    } catch (error: any) {
      console.error('DIAGNOSTIC: Supabase test error:', error);
      results.supabaseTest = { success: false, error: String(error) };
    }
    
    // Wait 2 seconds before next test
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Step 3: Test customer data and opt_in field
    console.log('DIAGNOSTIC: Step 3 - Testing opt_in field access');
    try {
      const optInResult = await (window as any).testOptInField(customerId, rooftopId);
      console.log('DIAGNOSTIC: Opt-in field test result:', optInResult.success ? 'PASSED' : 'FAILED', optInResult);
      results.optInFieldTest = optInResult;
    } catch (error: any) {
      console.error('DIAGNOSTIC: Opt-in field test error:', error);
      results.optInFieldTest = { success: false, error: String(error) };
    }
    
    // Summary - show results as we have them
    console.log('DIAGNOSTIC: Interim summary:');
    console.log('1. Webhook Connectivity:', results.webhookTest?.success ? '✅ PASSED' : '❌ FAILED');
    console.log('2. Supabase Connectivity:', results.supabaseTest?.success ? '✅ PASSED' : '❌ FAILED');
    console.log('3. Opt-in Field Access:', results.optInFieldTest?.success ? '✅ PASSED' : '❌ FAILED');
    
    // Now that we know capabilities, try the direct webhook and opt-in tests
    if (results.webhookTest?.success && results.supabaseTest?.success) {
      // Wait 2 seconds before next test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 4: Test direct webhook call with customer data
      console.log('DIAGNOSTIC: Step 4 - Testing direct webhook call');
      try {
        const directWebhookResult = await (window as any).testWebhook(customerId, rooftopId);
        console.log('DIAGNOSTIC: Direct webhook test result:', directWebhookResult);
        results.directWebhookTest = directWebhookResult;
      } catch (error: any) {
        console.error('DIAGNOSTIC: Direct webhook test error:', error);
        results.directWebhookTest = { success: false, error: String(error) };
      }
      
      // Wait 2 seconds before next test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 5: Test opt-in change
      console.log('DIAGNOSTIC: Step 5 - Testing opt-in change');
      try {
        const optInChangeResult = await (window as any).testOptInChange(customerId, rooftopId);
        console.log('DIAGNOSTIC: Opt-in change test result:', optInChangeResult);
        results.optInChangeTest = optInChangeResult;
      } catch (error: any) {
        console.error('DIAGNOSTIC: Opt-in change test error:', error);
        results.optInChangeTest = { success: false, error: String(error) };
      }
    } else {
      console.log('DIAGNOSTIC: Skipping direct webhook and opt-in tests due to failed prerequisites');
    }
  } catch (error: any) {
    console.error('DIAGNOSTIC: Test sequence error:', error);
    results.sequenceError = String(error);
  }
  
  // Final Summary
  console.log('DIAGNOSTIC: All tests completed - SUMMARY:');
  console.log('-------------------------------------------');
  console.log('1. Webhook Connectivity:', results.webhookTest?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('2. Supabase Connectivity:', results.supabaseTest?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('3. Opt-in Field Access:', results.optInFieldTest?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('4. Direct Webhook Call:', results.directWebhookTest?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('5. Opt-in Change Test:', results.optInChangeTest?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('-------------------------------------------');
  
  return results;
};

// Test if we can update the opt_in field
(window as any).testOptInField = async (customerId: number, rooftopId: string) => {
  console.log(`OPT-IN FIELD TEST: Testing update on customer_data for ${customerId}`);
  
  try {
    // First try to get the current value
    console.log('OPT-IN FIELD TEST: Getting current customer data');
    const { data: current, error: getError } = await supabase
      .from('customer_data')
      .select('id, first_name, last_name, opt_in')
      .eq('id', customerId)
      .eq('rooftop_id', rooftopId)
      .single();
    
    if (getError) {
      console.error('OPT-IN FIELD TEST: Error getting customer data:', getError);
      return { success: false, error: getError };
    }
    
    if (!current) {
      console.error('OPT-IN FIELD TEST: Customer not found');
      return { success: false, error: 'Customer not found' };
    }
    
    console.log('OPT-IN FIELD TEST: Current customer data:', current);
    
    // Now try to update by toggling the current value
    const newOptInValue = !current.opt_in;
    console.log(`OPT-IN FIELD TEST: Updating opt_in to ${newOptInValue}`);
    
    const { data: updateResult, error: updateError } = await supabase
      .from('customer_data')
      .update({ opt_in: newOptInValue })
      .eq('id', customerId)
      .eq('rooftop_id', rooftopId)
      .select();
    
    if (updateError) {
      console.error('OPT-IN FIELD TEST: Error updating opt_in:', updateError);
      return { success: false, error: updateError };
    }
    
    console.log('OPT-IN FIELD TEST: Update successful:', updateResult);
    
    return { 
      success: true, 
      originalValue: current.opt_in,
      newValue: newOptInValue,
      updateResult
    };
  } catch (error) {
    console.error('OPT-IN FIELD TEST: Unexpected error:', error);
    return { success: false, error: String(error) };
  }
};



